/*
 * GOODBUILD brand tokens. Section 7 of the specification.
 *
 * Rules encoded here rather than left to memory:
 *   - Four signal colours, one per surface. No fifth colour.
 *   - Flat fields and rules. No gradients, anywhere, ever.
 *   - Volt marks something live at this moment and nothing else.
 *   - Volt and Solar take Ink type, never white.
 *   - Every number is Martian Mono with tabular figures.
 *
 * **Dark throughout, which is a deliberate departure from the guidelines.**
 * Section 04 puts the store on Paper, and its argument is a good one: every
 * competitor is dark, and a page that is a grid of other studios' capsule art
 * shows that art best on Paper. The counter-argument, and the one that won, is
 * that this is a games store and the audience arrives expecting the room to be
 * dark — the differentiation was buying a first impression of "not a games
 * site" at full price. What it costs is recorded in
 * `apps/app/src/ui/test/store-and-spaces.test.ts` beside the test that used to
 * require Paper, so the decision is somewhere rather than nowhere.
 *
 * Three grounds, all near-black, each tinted a few points toward its own
 * channel: warm for the store's Solar, red for the creators' Hot, blue for the
 * developers' Build. A reader should not be able to name the tint and should
 * still know they have moved.
 */

:root {
  /* --- Signal colours ------------------------------------------------- */
  /* The store: games, purchase, verified. Primary. */
  --gb-build: #2b3dff;
  /* Creators: pages, board, commission, attribution. */
  --gb-hot: #ff2d6f;
  /* First Hundred, playtests, anything counting down. */
  --gb-solar: #ff7a00;
  /* Live only: streaming now, slots left, current version. Under 5% of any layout. */
  --gb-volt: #c6ff00;

  /* --- Ground --------------------------------------------------------- */
  --gb-ink: #0b0b12;
  --gb-paper: #fbfaf6;

  /* --- Ink ramp ------------------------------------------------------- */
  --gb-ink-70: #4a4a57;
  --gb-ink-40: #8c8c99;
  --gb-ink-15: #d9d9e0;
  --gb-ink-07: #ededf2;

  /* --- Type on signal colours ----------------------------------------- */
  /* Volt and Solar take Ink type. Build and Hot take Paper. Never invert these. */
  --gb-on-build: var(--gb-paper);
  --gb-on-hot: var(--gb-paper);
  --gb-on-solar: var(--gb-ink);
  --gb-on-volt: var(--gb-ink);

  /* --- Surface roles: light (store) ----------------------------------- */
  --gb-surface: var(--gb-paper);
  --gb-surface-raised: #ffffff;
  --gb-surface-sunken: var(--gb-ink-07);
  --gb-text: var(--gb-ink);
  --gb-text-muted: var(--gb-ink-70);
  --gb-text-faint: var(--gb-ink-40);
  --gb-rule: var(--gb-ink-15);
  /*
   * `--gb-accent-base` is the panel's colour, and `--gb-accent` is what a
   * component should actually read. They are the same value everywhere except
   * inside a flood band of the accent itself, where the two swap: on an orange
   * field the emphasis colour has to be the type colour, not orange again.
   *
   * The band can only express that swap because the `-base` pair is never
   * redefined — `--gb-accent: var(--gb-on-accent)` alongside
   * `--gb-on-accent: var(--gb-accent)` in one block is a cycle, and CSS throws
   * both away rather than resolving it.
   */
  --gb-accent-base: var(--gb-build);
  --gb-on-accent-base: var(--gb-on-build);
  --gb-accent: var(--gb-accent-base);
  --gb-on-accent: var(--gb-on-accent-base);

  --gb-signal-live: var(--gb-volt);
  --gb-on-signal-live: var(--gb-on-volt);

  /* --- Type ----------------------------------------------------------- */
  --gb-font-display: 'Archivo', ui-sans-serif, system-ui, sans-serif;
  --gb-font-text: 'Archivo', ui-sans-serif, system-ui, sans-serif;
  /* Every number in the product is set in this face. */
  --gb-font-mono: 'Martian Mono', ui-monospace, 'SFMono-Regular', monospace;

  --gb-display-min: 3rem; /* 48px */
  --gb-display-max: 6rem; /* 96px */
  --gb-headline-min: 1.75rem; /* 28px */
  --gb-headline-max: 2.5rem; /* 40px */
  --gb-subhead: 1.25rem; /* 20px */
  --gb-lead: 1.375rem; /* 22px */
  --gb-body: 1rem; /* 16px */
  --gb-ui-label: 0.75rem; /* 12px */
  --gb-data-label: 0.6875rem; /* 11px */

  --gb-tracking-display: -0.02em;
  --gb-tracking-data: 0.12em;

  /* --- Geometry ------------------------------------------------------- */
  /* Rectangles throughout. The mission patch is the only circle in the system,
     which is what makes it read as an award rather than as UI. */
  --gb-radius: 0;
  --gb-radius-patch: 50%;
  --gb-rule-width: 1px;

  --gb-space-1: 0.25rem;
  --gb-space-2: 0.5rem;
  --gb-space-3: 0.75rem;
  --gb-space-4: 1rem;
  --gb-space-6: 1.5rem;
  --gb-space-8: 2rem;
  --gb-space-12: 3rem;
  --gb-space-16: 4rem;

  --gb-focus-width: 3px;
  --gb-focus-offset: 2px;

  /*
   * The scrollbar (#95).
   *
   * Ten pixels: narrower than a platform default, wide enough to grab with a
   * pointer, and the same on every surface so that a table scrolling sideways
   * inside a page that is scrolling down does not draw two different bars.
   *
   * **The Ink ramp here rather than the surface roles**, and the exception is
   * the whole reason these are tokens at all. A panel re-points them to its own
   * neutrals in the `[data-gb-space]` block below, which is what makes an inner
   * scroller belong to the surface it is on. What that cannot reach is the
   * *viewport's* bar in the React app: `data-gb-space` sits on a `div` inside
   * `body` there — that is what lets one document hold a creator's page and the
   * store's chrome — so the page scrollbar resolves against `:root` and would
   * take `--gb-surface-sunken`'s light-store leftovers. All three grounds are
   * Ink pulled a few points one way or another, so the ramp is the right
   * default for it and for anything else that never reaches a panel.
   */
  --gb-scrollbar-size: 0.625rem;
  --gb-scrollbar-track: var(--gb-ink);
  --gb-scrollbar-thumb: var(--gb-ink-70);
  --gb-scrollbar-thumb-hover: var(--gb-ink-40);

  /* --- Layout ----------------------------------------------------------- */
  /*
   * How wide the content runs, and how far it stands off the window edge.
   *
   * **The measure is the whole window.** It was 72rem on most pages and 100rem
   * on the ones that showed a grid, which on a large monitor left a third of the
   * screen empty and showed four capsules where there was room for seven — the
   * clearest way a store ends up looking like an internal tool. A games site
   * fills the screen it is given.
   *
   * The number lived in eleven stylesheets before this, which is how the footer
   * ended up narrower than the page above it once already. One token now, read
   * by every page shell and by the footer, so they cannot disagree.
   *
   * Line length is a separate question and it is answered by `--gb-prose`
   * below, which a paragraph reads rather than declaring a character count of
   * its own. The legal layout is the one place that still names a width, at the
   * measure its source is hard-wrapped to.
   *
   * The gutter grows with the window so content is never flush to the edge on a
   * wide screen, and never wastes a phone's width on margins.
   */
  --gb-measure: 100%;
  --gb-gutter: clamp(var(--gb-space-6), 3vw, var(--gb-space-16));

  /*
   * How wide a paragraph runs.
   *
   * **It was 62ch on body copy and 52ch on a lead**, hard-wired into the type
   * rules, and the reasoning was sound in the abstract: long lines are the most
   * common reason a page nobody read went unread. What it produced on the pages
   * that actually exist was not that. The publishing pitch and the about page
   * are full-width sections of argument with nothing beside them — no figure,
   * no capsule, no pull quote — so a 62-character cap left every one of them as
   * a narrow strip down the left of a wide screen, with two thirds of the page
   * empty to the right of it. That does not read as a considered measure. It
   * reads as a layout that failed to load.
   *
   * So the measure is the column the paragraph is in. On the editorial pages
   * that is the page, which is what those pages wanted; inside a two-column
   * grid it is the column, unchanged; inside the app's content area it is the
   * area beside the navigation, also unchanged. The pages that were already
   * right stay right, and the ones that were a strip are not a strip.
   *
   * **A page that is genuinely a document overrides this on its own container**
   * rather than everything else compensating for it. `Legal.astro` is the case:
   * three of the longest documents on the site, hard-wrapped at source, and it
   * has always set its own 78ch on `.document` rather than relying on the type
   * rules — which is the pattern, not an exception to it.
   */
  --gb-prose: 100%;

  /* --- Motion ---------------------------------------------------------- */
  /*
   * Three durations and three curves, so motion is as constrained as colour is.
   *
   * The brand is flat, rectangular and typographic; its motion has to be the
   * same, which means things move and change opacity but never bounce, blur or
   * spring. `--gb-ease-out` decelerates into place and is the default for
   * anything entering. `--gb-ease` is symmetric, for a state changing in place.
   * `--gb-ease-emphasis` overshoots slightly and is reserved for a moment worth
   * marking — a step accepted, an application sent.
   *
   * Everything built from these is switched off wholesale by the
   * `prefers-reduced-motion` block below, which is why no component needs to
   * remember to do it.
   */
  --gb-motion-fast: 120ms;
  --gb-motion: 200ms;
  --gb-motion-slow: 340ms;
  --gb-motion-scene: 820ms;
  --gb-camera-push: 24s;

  --gb-ease: cubic-bezier(0.4, 0, 0.2, 1);
  --gb-ease-out: cubic-bezier(0.16, 1, 0.3, 1);
  --gb-ease-emphasis: cubic-bezier(0.34, 1.4, 0.64, 1);

  /* Stagger between siblings revealing as a group. */
  --gb-stagger: 60ms;
}

/*
 * Dark surface roles. Applied by the launcher throughout, and available to the
 * web app for any surface that deliberately runs on Ink. This is a surface
 * role swap, not a second palette: the four signal colours do not change.
 */
[data-gb-surface='ink'] {
  --gb-surface: var(--gb-ink);
  --gb-surface-raised: #15151f;
  --gb-surface-sunken: #06060b;
  --gb-text: var(--gb-paper);
  --gb-text-muted: #a8a8b8;
  --gb-text-faint: #6e6e80;
  --gb-rule: #2a2a38;
}

/* --- Document base ------------------------------------------------------ */

/*
 * The layer every surface needs and none of them should have to remember.
 *
 * This lived in the Next app's `globals.css`, which did not survive the move to
 * Astro — and the storefront imports this file and nothing else. So for a while
 * anything without a `.gb-` class set in the browser's default serif, and the
 * wordmark rendered in the user agent's visited-link purple, because it is an
 * anchor and nothing told it otherwise.
 *
 * It belongs beside the tokens rather than in an application. A brand that only
 * applies when an app remembers to import a second stylesheet is not a brand,
 * and the way this went wrong is exactly the way it would go wrong again.
 */

html {
  -webkit-text-size-adjust: 100%;
}

body {
  margin: 0;
  min-height: 100dvh;
  background: var(--gb-surface);
  color: var(--gb-text);
  font-family: var(--gb-font-text);
  font-size: var(--gb-body);
  font-weight: 400;
  line-height: 1.55;
}

/*
 * A link takes the colour of the type around it.
 *
 * Size, weight and rules carry emphasis in this system; a coloured link would
 * be a fifth signal colour chosen by the user agent. It is also what keeps the
 * wordmark solid — section 02 is explicit that the mark is Ink on light and
 * Paper on dark and never anything else, and it is wrapped in an `<a>`.
 */
a {
  color: inherit;
  text-decoration-thickness: 1px;
  text-underline-offset: 0.2em;
}

/* --- Base type roles --------------------------------------------------- */

.gb-display,
.gb-headline,
.gb-subhead {
  font-family: var(--gb-font-display);
  text-transform: uppercase;
  font-variation-settings:
    'wdth' 125,
    'wght' 900;
  letter-spacing: var(--gb-tracking-display);
  /* Brand guidelines section 05: display sets at 0.86, not the 0.95 that reads
     as ordinary heading leading. Tight leading is most of what makes the
     display voice loud, and it only works when lines are set to stack. */
  line-height: 0.86;
  margin: 0;
}

.gb-display {
  font-size: clamp(var(--gb-display-min), 7vw, var(--gb-display-max));
  letter-spacing: -0.025em;
}

.gb-headline {
  font-size: clamp(var(--gb-headline-min), 4vw, var(--gb-headline-max));
  line-height: 0.9;
  letter-spacing: -0.015em;
}

/*
 * Section headings sit at width 112, not 125.
 *
 * The guidelines separate the two deliberately: 125 is the display voice and
 * belongs to the wordmark and page headlines, and using it for every heading
 * flattens the hierarchy so nothing reads as the loudest thing on the page.
 */
.gb-subhead {
  font-size: var(--gb-subhead);
  font-variation-settings:
    'wdth' 112,
    'wght' 800;
  line-height: 1;
  letter-spacing: 0.02em;
}

.gb-body {
  font-family: var(--gb-font-text);
  font-size: var(--gb-body);
  font-weight: 400;
  line-height: 1.55;
  /* The column it is in, rather than a fixed count of characters. See
     `--gb-prose` above for what the fixed one did to a full-width page. */
  max-width: var(--gb-prose);
}

/*
 * The lead paragraph. Medium weight, tighter leading, shorter measure.
 *
 * A separate role from body because the guidelines set it at weight 500 across
 * 52 characters: it is the sentence that has to land before anybody decides
 * whether to keep reading.
 */
.gb-lead {
  font-family: var(--gb-font-text);
  font-size: var(--gb-lead);
  font-variation-settings:
    'wdth' 100,
    'wght' 500;
  line-height: 1.35;
  max-width: var(--gb-prose);
  margin: 0;
}

/*
 * The eyebrow. Martian Mono, wide tracking, above a heading.
 *
 * Distinct from .gb-data-label, which labels a measurement. This labels a
 * section, and carries slightly more weight and tracking so it holds its own
 * above display type.
 */
/*
 * The 70% ramp value, not the 40%.
 *
 * The guidelines set every label that carries meaning in `--ink-70`: the stat
 * label, the table header, a swatch's hex and its use note. `--ink-40` appears
 * in that document exactly twice, on the page number and on a specimen tag —
 * furniture, not content. This role had the 40%, which is both a departure from
 * the guidelines and a legibility problem: #8C8C99 on Paper is 3.2:1, and this
 * is 11px uppercase Martian Mono with wide tracking, which is the hardest thing
 * in the system to read. #4A4A57 is 8.4:1 and is the same ramp.
 */
.gb-eyebrow {
  font-family: var(--gb-font-mono);
  font-size: var(--gb-data-label);
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--gb-text-muted);
  display: block;
  margin: 0;
}

.gb-ui-label {
  font-family: var(--gb-font-text);
  font-size: var(--gb-ui-label);
  font-weight: 600;
}

/*
 * Every number in the product: playtime, prices, slot counts, review scores,
 * version strings, commission rates, viewer counts, percentages.
 *
 * Tabular figures so a counter ticking from 38 to 37 does not shift the
 * layout. The platform's argument is that it measures what nobody else
 * measures, so measurements get their own voice.
 */
.gb-stat,
.gb-data-label {
  font-family: var(--gb-font-mono);
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum' 1;
}

.gb-stat {
  font-weight: 700;
  line-height: 1;
}

.gb-data-label {
  font-size: var(--gb-data-label);
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: var(--gb-tracking-data);
}

/*
 * Unflattering numbers get the same type, size and weight as flattering ones
 * (hard rule 14). There is deliberately no muted or smaller variant of
 * .gb-stat for negative values, and there must never be one.
 */

/* --- Quality floor ----------------------------------------------------- */

/* The focus ring is the panel's accent, so it stays inside the panel's three
   colours and is legible on that panel's ground. */
:where(a, button, input, select, textarea, summary, [tabindex]):focus-visible {
  outline: var(--gb-focus-width) solid var(--gb-accent);
  outline-offset: var(--gb-focus-offset);
}

@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
    scroll-behavior: auto !important;
  }
}

/* --- Scrollbars --------------------------------------------------------- */

/*
 * The one piece of chrome the product had not drawn (#95).
 *
 * Every surface here is near-black, and a platform scrollbar on it is a wide
 * light-grey rail with a rounded lozenge and, on Windows, two arrow buttons —
 * three shapes the rest of this system does not contain. On a page that is
 * otherwise flat rectangles it is the loudest thing on screen, and in the
 * launcher, which is a window with no browser furniture around it, it is the
 * only thing that says "this is a web page".
 *
 * **Both mechanisms, deliberately, and they do not fight.** `scrollbar-width`
 * and `scrollbar-color` are the standard properties and are what Firefox reads;
 * `::-webkit-scrollbar` is the older pseudo-element family and is what WebKit
 * reads — and what Chromium prefers whenever any rule in that family exists,
 * ignoring the standard properties entirely. So each engine takes one of the
 * two, both are pointed at the same tokens, and neither can be forgotten
 * without a whole engine reverting to the platform bar.
 *
 * Colours come from the tokens rather than being written out, which is what
 * makes a table scrolling sideways inside the developers' portal draw a blue-
 * black rail and the same table in the store draw a warm-black one. Section 04
 * again: a person should know which side of the product they are on before
 * reading a word, and that includes the furniture.
 */

/*
 * `color-scheme` first, and it is not redundant.
 *
 * It is what tells the engine that this document is dark, which sets the
 * default rendering of every control the stylesheet below does not reach — a
 * date picker, a `<select>` popup, the inner scrollbar of a `<textarea>`, and
 * the overlay scrollbars macOS draws over content, which no CSS can style.
 * Without it those all come out light on a near-black page.
 */
html {
  color-scheme: dark;
  scrollbar-width: thin;
  scrollbar-color: var(--gb-scrollbar-thumb) var(--gb-scrollbar-track);
}

/*
 * Everything on a panel takes that panel's colours, including its own bar.
 *
 * Both halves are re-pointed here rather than only `scrollbar-color`, because
 * the two engines read different things: Firefox reads the property, and
 * Chromium and WebKit resolve the custom properties on the scrolling element
 * through the pseudo-elements below. Setting one and not the other would give
 * one engine the panel's neutrals and the other the Ink ramp.
 *
 * Custom properties inherit, so this reaches every nested scroller — the
 * filtered country list, a table running wider than its column, the document
 * reader — without any of them naming it. And on the storefront, where
 * `data-gb-space` is on `<html>` itself, it reaches the page bar too.
 */
[data-gb-space] {
  --gb-scrollbar-track: var(--gb-surface-sunken);
  --gb-scrollbar-thumb: var(--gb-text-faint);
  --gb-scrollbar-thumb-hover: var(--gb-text-muted);
  scrollbar-color: var(--gb-scrollbar-thumb) var(--gb-scrollbar-track);
}

::-webkit-scrollbar {
  width: var(--gb-scrollbar-size);
  height: var(--gb-scrollbar-size);
}

::-webkit-scrollbar-track {
  background: var(--gb-scrollbar-track);
}

/*
 * A rectangle, like everything else. `--gb-radius` is 0 and the mission patch
 * is the only circle in the system, which is what makes it read as an award
 * rather than as UI — a rounded scrollbar thumb would be the second.
 *
 * The border is the track colour rather than transparent: `background-clip:
 * padding-box` on a transparent border is the usual trick and leaves a hairline
 * of the *page* showing through at the edges of an inner scroller, which is
 * visible wherever a panel is raised above its ground. Painting the inset in
 * the track's own colour costs nothing and cannot do that.
 */
::-webkit-scrollbar-thumb {
  background: var(--gb-scrollbar-thumb);
  border: 2px solid var(--gb-scrollbar-track);
  border-radius: var(--gb-radius);
}

::-webkit-scrollbar-thumb:hover {
  background: var(--gb-scrollbar-thumb-hover);
}

/* Where a vertical and a horizontal bar meet. Unstyled it is a light square. */
::-webkit-scrollbar-corner {
  background: var(--gb-scrollbar-track);
}

/*
 * The arrow buttons at each end, which Windows draws and nothing here wants.
 *
 * They are two more shapes, they are the smallest hit targets on the screen,
 * and scrolling by one line at a time is not a thing anybody does on a page of
 * this shape. Removing them also makes the bar the same on all three platforms,
 * which matters most in the launcher — the same window, three operating
 * systems, and until now three different scrollbars in it.
 */
::-webkit-scrollbar-button {
  display: none;
}

/* --- Document polish ---------------------------------------------------- */

/*
 * The rest of what `globals.css` carried, moved here for the same reason as the
 * base above: it is brand behaviour, not application behaviour.
 *
 * All of it is built from the three durations and three curves declared at the
 * top of this file, and the reduced-motion block above switches the lot off —
 * which is why only the transforms are cancelled again below, since that block
 * makes motion instant rather than absent.
 */

/*
 * The underline on a prose link fades in rather than appearing.
 *
 * `text-decoration` as a whole cannot be animated but `text-decoration-color`
 * can, so the rule is always there and only its colour changes. That is why
 * this is not the growing-rule effect the navigation uses: a pseudo-element
 * rule breaks apart on a link that wraps across two lines, and prose links
 * wrap. The other common trick — a `linear-gradient` sized from 0% — is a
 * gradient, and this brand does not have gradients.
 *
 * Deliberately not every anchor: navigation, cards and buttons have their own
 * hover states and would end up with two.
 */
.gb-body a:not([class]),
.gb-lead a:not([class]) {
  text-decoration: underline;
  text-decoration-color: transparent;
  text-decoration-thickness: 1px;
  text-underline-offset: 0.2em;
  transition: text-decoration-color var(--gb-motion) var(--gb-ease-out);
}

.gb-body a:not([class]):hover,
.gb-lead a:not([class]):hover,
.gb-body a:not([class]):focus-visible,
.gb-lead a:not([class]):focus-visible {
  text-decoration-color: currentColor;
}

/*
 * Everything clickable acknowledges the press. One frame of downward movement,
 * which is the cheapest way for an interface to feel like it is made of
 * something, and whose absence is most of why a page reads as a document.
 */
button:not(:disabled):active,
a[class*='cta']:active,
a[class*='button']:active {
  transform: translateY(1px);
}

@media (prefers-reduced-motion: reduce) {
  button:not(:disabled):active,
  a[class*='cta']:active,
  a[class*='button']:active {
    transform: none;
  }

  .gb-body a:not([class]),
  .gb-lead a:not([class]) {
    text-decoration-color: currentColor;
    transition: none;
  }
}

/* --- Audience spaces ---------------------------------------------------- */

/*
 * Section 04: one channel colour per surface, so a person knows where they are
 * before they read a word. Three audiences, three grounds.
 *
 * These swap surface *roles*, exactly like the launcher's Ink swap above. No
 * new colours are introduced, because "adding a fifth breaks wayfinding" and
 * the four channels are the whole system.
 *
 * --- Three colours per panel, and only three -----------------------------
 *
 * Each panel gets a ground, a type colour and one accent, and nothing on that
 * panel may reach for a channel outside its own set:
 *
 *   store       Paper  #fbfaf6   Ink   #0b0b12   Solar  #ff7a00
 *   creators    Paper tinted Hot   Ink #0b0b12   Hot    #ff2d6f
 *   developers  Ink    #0b0b12   Paper #fbfaf6   Build  #2b3dff
 *
 * Muted type, faint type, rules and the raised and sunken surfaces are tints of
 * that panel's own two neutrals, or of its accent. They are not further
 * colours; three *hues* is the rule, not three hex values.
 *
 * This is why almost nothing in the app should name a channel directly.
 * `--gb-accent` is how a component asks for "this panel's colour" — a rule that
 * hardcodes `var(--gb-build)` is blue on the store, where blue is not in the
 * set, and is the way the constraint would quietly rot. `test/palette.test.ts`
 * fails the build if a stylesheet names a channel outside the small list of
 * places allowed to.
 *
 * Solar carries the store rather than Build, which is a deliberate departure
 * from section 04's "the store is Build". Build now belongs to developers
 * alone, and a panel's colour is only wayfinding if no two panels share one.
 * Volt keeps its meaning and is used by neither: it is an alarm, not a palette,
 * and a lime buy button would drain the live badges of the one thing they say.
 */

/*
 * The store. Paper, Ink, Solar.
 *
 * Stated explicitly rather than left implicit so that a page which forgets to
 * declare a space still lands somewhere deliberate.
 *
 * Solar is the warmest of the four and the only one that reads as retail: it is
 * already the platform's countdown colour, which is what a storefront is full
 * of — a sale ending, a playtest wave closing, a First Hundred window. It takes
 * Ink type, so a buy button is orange with black on it and clears contrast
 * comfortably, which Build on Paper never quite did at label size.
 */
[data-gb-space='store'] {
  /* Ink warmed a few points toward Solar. Paper type reads 18.4:1 on it. */
  --gb-surface: #120e09;
  --gb-surface-raised: #1f1a13;
  --gb-surface-sunken: #0a0805;
  --gb-text: var(--gb-paper);
  --gb-text-muted: #a8a8b8;
  --gb-text-faint: #6e6e80;
  --gb-rule: #38302a;
  --gb-accent-base: var(--gb-solar);
  --gb-on-accent-base: var(--gb-on-solar);
  --gb-accent: var(--gb-accent-base);
  --gb-on-accent: var(--gb-on-accent-base);
}

/*
 * Creators. Hot throughout.
 *
 * The ground was a Paper tinted towards Hot and is now an Ink tinted the same
 * way — the tint survived the move to dark, and it is still a tint of a channel
 * rather than a new colour. Hot reads 5.4:1 on it, up from 3.4:1 on the light
 * version, which is the one thing this change straightforwardly improved.
 */
[data-gb-space='creators'] {
  --gb-surface: #120b0f;
  --gb-surface-raised: #1f151b;
  --gb-surface-sunken: #0b0607;
  --gb-text: var(--gb-paper);
  --gb-text-muted: #a8a8b8;
  --gb-text-faint: #6e6e80;
  --gb-rule: #382a31;
  --gb-accent-base: var(--gb-hot);
  --gb-on-accent-base: var(--gb-on-hot);
  --gb-accent: var(--gb-accent-base);
  --gb-on-accent: var(--gb-on-accent-base);
}

/*
 * Developers. Ink, and the only space this change left alone.
 *
 * The guidelines assign no channel to the developer surface, so rather than
 * invent one it takes the Ink role swap. That is also the honest read of what
 * this space is: not a shop window but a working tool, used for long sessions
 * the way the launcher is, and the launcher is Ink for exactly that reason.
 *
 * It is now the neutral one of the three: the other two are Ink pulled a few
 * points warm and a few points red, and this is Ink itself.
 */
[data-gb-space='developers'] {
  --gb-surface: var(--gb-ink);
  --gb-surface-raised: #15151f;
  --gb-surface-sunken: #06060b;
  --gb-text: var(--gb-paper);
  --gb-text-muted: #a8a8b8;
  --gb-text-faint: #6e6e80;
  --gb-rule: #2a2a38;
  --gb-accent-base: var(--gb-build);
  --gb-on-accent-base: var(--gb-on-build);
  --gb-accent: var(--gb-accent-base);
  --gb-on-accent: var(--gb-on-accent-base);
}

/* A themed shell paints its own ground; body alone would leave the gap below
   short pages showing the default surface. */
[data-gb-space] {
  background: var(--gb-surface);
  color: var(--gb-text);
}
